/* Shared constants, motion props, and pure helpers for the DAW components.
 * Hoisted to module scope to avoid allocating new objects on every render. */

export const TOTAL_CHANNELS = 64;
export const METER_BAR_COUNT = 14;
export const RULER_TICK_COUNT = 32;
export const WAVE_BIN_COUNT = 80;
export const TIMELINE_BAR_GRID = 16;
export const TIMELINE_VISIBLE_LANES = 24;

export const EFFECT_LIST = [
  { key: "autotune",   label: "AutoTune / Pitch", hint: "+14¢ vibrato @ 4.5Hz" },
  { key: "eq",         label: "EQ",               hint: "Low +4 · Mid −2.5 · High +3" },
  { key: "compressor", label: "Compressor",       hint: "−18dB · 4:1" },
  { key: "reverb",     label: "Reverb",           hint: "2.4s plate · 0.35 send" },
  { key: "delay",      label: "Delay",            hint: "380ms · 40% feedback" },
  { key: "limiter",    label: "Limiter",          hint: "−3dB · 20:1" },
];

// iter-220 — Honest preview of what Demucs htdemucs_6s ACTUALLY returns.
// Pre-iter-220 we showed 12 stems (Vocals, Drums, Bass, Piano, Guitar,
// Choir, Strings, FX, Lead, BGV, Perc, Master). The button promised
// "Extract up to 12 Stems" but only 6 ever come back, leaving the
// other 6 chips unfilled and users thinking the extraction failed.
// Demucs 6s returns EXACTLY these 6 stems: vocals, drums, bass, other,
// guitar, piano. Matching the preview to reality removes the false
// promise and the resulting "no stems appeared" confusion.
export const STEMS_LIST = ["Vocals","Drums","Bass","Other","Guitar","Piano"];

export const CHANNEL_TINTS = [
  { ring: "ring-sky-400/40",     grad: "from-sky-500/30 to-sky-900/10",         dot: "bg-sky-300",     glow: "rgba(56,189,248,0.85)",  accent: "#38bdf8" },
  { ring: "ring-fuchsia-400/40", grad: "from-fuchsia-500/30 to-fuchsia-900/10", dot: "bg-fuchsia-300", glow: "rgba(232,121,249,0.85)", accent: "#e879f9" },
  { ring: "ring-emerald-400/40", grad: "from-emerald-500/30 to-emerald-900/10", dot: "bg-emerald-300", glow: "rgba(52,211,153,0.85)",  accent: "#34d399" },
  { ring: "ring-amber-400/40",   grad: "from-amber-500/30 to-amber-900/10",     dot: "bg-amber-300",   glow: "rgba(252,211,77,0.85)",  accent: "#fcd34d" },
  { ring: "ring-rose-400/40",    grad: "from-rose-500/30 to-rose-900/10",       dot: "bg-rose-300",    glow: "rgba(251,113,133,0.85)", accent: "#fb7185" },
  { ring: "ring-cyan-400/40",    grad: "from-cyan-500/30 to-cyan-900/10",       dot: "bg-cyan-300",    glow: "rgba(34,211,238,0.85)",  accent: "#22d3ee" },
];

export const MASTERING_TARGET_LABELS = [
  { key: "streaming", label: "Streaming",     sub: "−14 LUFS" },
  { key: "album",     label: "Album",         sub: "−16 LUFS" },
  { key: "loud",      label: "Loud",          sub: " −9 LUFS" },
  { key: "podcast",   label: "Podcast",       sub: "−19 LUFS" },
];

export const BUSY_LABELS = {
  voice: "Training voice model",
  stems: "Splitting stems",
  master: "Measuring & mastering",
};

export const STEM_STATUS_LABELS = {
  uploading: "Uploading audio to fal.ai…",
  submitted: "Submitted to Demucs",
  queued: "Queued at fal.ai",
  progress: "Splitting stems",
  downloading: "Loading stems into channels",
};

/* Motion presets hoisted so framer-motion doesn't get a new object each render */
export const HEADER_INIT = { opacity: 0, y: -14 };
export const HEADER_ANIM = { opacity: 1, y: 0 };
export const STRIP_HOVER = { y: -2 };
export const FADE_IN = { opacity: 0 };
export const FADE_FULL = { opacity: 1 };

/* Pure helpers */
export function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

export function fmtTime(sec) {
  const s0 = Number.isFinite(sec) ? sec : 0;
  const m = Math.floor(s0 / 60);
  const s = Math.floor(s0 % 60);
  const ms = Math.floor((s0 - Math.floor(s0)) * 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(2, "0")}`;
}

export const padCh = (i) => String(i + 1).padStart(2, "0");
export const tintFor = (i) => CHANNEL_TINTS[i % CHANNEL_TINTS.length];
export const trimName = (name, max = Infinity) => (name ? name.replace(/\.[^.]+$/, "").slice(0, max) : null);

export const fmtLufs = (v) => (Number.isFinite(v) ? `${v.toFixed(1)} LUFS` : "—");
export const fmtDb   = (v) => (Number.isFinite(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(1)} dB` : "—");
